/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable jsx-a11y/no-onchange */
import React, { useEffect, useState } from 'react'
import intl from 'react-intl-universal'
import {
  getDictionaries,
  addCustomWords,
  deleteCustomWords,
} from '~/api/spellcheck'
import { ManageCustomWordsModal } from './ManageCustomWordsModal'
import './ArcXpSpellcheckSettings.styles.scss'
import { ComposerSettings } from '~/types'

type Props = ComposerSettings['proofreaders']['arcSpellcheck'] & {
  onChange: (
    arcSpellcheck: ComposerSettings['proofreaders']['arcSpellcheck']
  ) => void
}

const wordsRegex = /([!`\-_."'#%:;<>=@{}~$()*+/\\?[\]^|]+)/

const ArcXpSpellcheckSettings: React.FC<Props> = (props) => {
  const baseLang = props.language ? props.language.split(',')[0] : 'en_US'
  const [dictionaries, setDictionaries] = useState<any>()
  const [customWords, setCustomWords] = useState('')
  const [data, setData] = useState<any>(null)
  const [baseLanguage, setBaseLanguage] = useState(baseLang)
  const [inputError, setInputError] = useState('')
  const [addedCustomWordsState, setAddedCustomWords] = useState<string>('')
  const [isOpen, setIsOpen] = useState(false)
  const toogle = () => setIsOpen(!isOpen)

  useEffect(() => {
    async function callApi() {
      const apiResponse = await getDictionaries()
      setDictionaries(
        !apiResponse.error ? Object.values(apiResponse) : apiResponse
      )
    }

    props.onChange({
      language: baseLanguage,
    })

    callApi()
  }, [])

  const handleAddCustomWords = async () => {
    if (customWords && !wordsRegex.test(customWords)) {
      const data = await addCustomWords(customWords)
      setAddedCustomWords(customWords)
      setCustomWords('')
      setData(data)
    } else {
      setInputError(intl.get('SpellcheckSettings.customWords.error'))
    }
  }

  const handleUndoCustomWords = async () => {
    if (addedCustomWordsState !== '') {
      const data = await deleteCustomWords(addedCustomWordsState)
      if (data.deleted_words) {
        setAddedCustomWords('')
      }
      setData(data)
    }
  }

  const handleBaseSelectChange = (event) => {
    setBaseLanguage(event.target.value)

    props.onChange({
      language: event.target.value,
    })
  }

  const handleCustomWords = (event) => {
    if (inputError) {
      setInputError('')
    }
    if (data) {
      setData(null)
    }
    setCustomWords(event.target.value)
  }

  return dictionaries?.length > 0 && !dictionaries?.error ? (
    <div className="arcOptions">
      <div className="mb-4">
        <label htmlFor="baseLanguage">
          {intl.get('SpellcheckSettings.baseLanguage')}
        </label>
        <select
          className="form-control"
          onChange={handleBaseSelectChange}
          defaultValue={baseLanguage}
          name="drop1"
          id="baseLanguage"
          data-testid="baseLanguage"
        >
          {dictionaries?.map((item) => (
            <option key={item.code} value={item.code}>
              {item.name}
            </option>
          ))}
        </select>
      </div>
      <div className="mb-4">
        <label htmlFor="custom-words">
          {intl.get('SpellcheckSettings.customWords')}
        </label>
        <input
          id="custom-words"
          data-testid="custom-words"
          className={`form-control mb-3 ${inputError ? 'inputError' : ''}`}
          type="text"
          placeholder={intl.get('settings.spellchecker.customWordsDescription')}
          value={customWords}
          onChange={handleCustomWords}
        />
        {inputError && (
          <div className="inputErrorMessage mb-4">
            <span>{inputError}</span>
          </div>
        )}
        {data?.new_words?.length > 0 && (
          <div
            data-testid="spellcheck-add-custom-words-success"
            className="d-flex align-items-center justify-content-between mb-4 custom-words-success"
          >
            <span>
              {intl.get('SpellcheckSettings.customWords.added')}{' '}
              {data?.new_words.join(', ')}
            </span>
            {addedCustomWordsState !== '' && (
              <input
                className="btn btn-sm btn-secondary ng-binding"
                type="button"
                value={intl.get('SpellcheckSettings.customWords.added.undo')}
                title={intl.get(
                  'SpellcheckSettings.customWords.added.undo.tooltip'
                )}
                id="undo-add-custom-word"
                data-testid="undo-add-custom-word"
                onClick={handleUndoCustomWords}
              />
            )}
          </div>
        )}
        {data?.deleted_words && (
          <div className="mb-4 custom-words-success">
            <span>
              {intl.get('SpellcheckSettings.customWords.added.undo.success', {
                removedWord: data?.words.join(', '),
              })}
            </span>
            <br />
          </div>
        )}
        {data?.existing_words?.length > 0 && (
          <div className="mb-4 custom-words-error">
            <span>
              {intl.get('SpellcheckSettings.customWords.notAdded')}
              {': '}
              {data?.existing_words.map((word) => word + ' ')}
            </span>
            <br />
          </div>
        )}
        {data?.error && (
          <div
            className="mb-4 errorMessage"
            data-testid="spellcheck-add-custom-words-error"
          >
            <span>{data.error}</span>
          </div>
        )}
        <input
          className="btn btn-md btn-secondary ng-binding mr-2"
          type="button"
          value={intl.get('settings.spellchecker.customWordsButton')}
          id="add-custom-word"
          data-testid="add-custom-word"
          disabled={!customWords}
          onClick={handleAddCustomWords}
        />
        <input
          className="btn btn-md btn-secondary ng-binding"
          type="button"
          value={intl.get('settings.spellchecker.manageCustomWordsButton')}
          id="manage-custom-word"
          data-testid="manage-custom-word"
          onClick={toogle}
        />
      </div>
      <ManageCustomWordsModal isOpen={isOpen} setIsOpen={toogle} />
    </div>
  ) : dictionaries?.error ? (
    <span
      className="mb-4 errorMessage"
      data-testid="spellcheck-add-custom-words-error"
    >
      {dictionaries.error}
    </span>
  ) : (
    <div>{intl.get('Spellcheck.modal.loading')}</div>
  )
}

export default ArcXpSpellcheckSettings
